import { useQuery } from '@tanstack/react-query'
import req from '../../../api/request'
import { queryKeys } from '../../../config/queryKeys'

interface ScheduleSettingsResponse {
  settings?: {
    game?: {
      scoring?: {
        allow_placeholder_visitor?: boolean | null
      }
    }
  }
}

/**
 * Whether the competition permits placeholder-visitor games.
 *
 * The setting lives on the competition (Competition Details > Settings > Game
 * Setup Settings), so creating a game has to read it from the SELECTED
 * schedule -- there is no game yet to read it from.
 *
 * Defaults to `false` while loading or on error: showing the toggle enabled
 * and letting the API refuse the save is worse than showing it disabled, since
 * the admin would lose the name they typed. The disabled state explains why.
 */
export const useAllowPlaceholderVisitor = (
  scheduleId?: string | null,
  enabled = true
) => {
  const query = useQuery<ScheduleSettingsResponse | null>({
    queryKey: queryKeys.scheduleSettings.bySchedule(scheduleId ?? null),
    queryFn: async () => {
      const { data } = await req(`/schedules/${scheduleId}/settings`)
      return data as ScheduleSettingsResponse
    },
    enabled: Boolean(scheduleId) && enabled,
  })

  return {
    allowPlaceholderVisitor: Boolean(
      query.data?.settings?.game?.scoring?.allow_placeholder_visitor
    ),
    isLoadingSetting: query.isLoading,
  }
}

export default useAllowPlaceholderVisitor
